import React, { useRef } from 'react';

const MessageInput = ({
    replyContent,
    onReplyChange,
    onReplySubmit,
    attachments,
    onAddAttachment,
    onRemoveAttachment,
    showEmojiPicker,
    onToggleEmojiPicker,
    onEmojiSelect,
    showMediaOptions,
    onToggleMediaOptions,
    isRecording,
    recordingDuration,
    onStartRecording,
    onStopRecording,
    onCancelRecording,
    recordingLevel,
    disabled
}) => {
    const fileInputRef = useRef(null);

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        // Create a mock event object for compatibility
        const mockEvent = { target: { files } };
        onAddAttachment(mockEvent);
        event.target.value = ''; // Reset input
    };

    const formatRecordingTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="wzc-input-container">
            {/* Attachments Preview */}
            {attachments.length > 0 && (
                <div className="wzc-attachments-preview">
                    {attachments.map((file, index) => (
                        <div key={index} className="wzc-attachment-item">
                            <span className="wzc-attachment-name">{file.name}</span>
                            <button
                                className="wzc-attachment-remove"
                                onClick={() => onRemoveAttachment(index)}
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Media Options */}
            {showMediaOptions && (
                <div className="wzc-media-options">
                    <button
                        className="wzc-media-btn"
                        onClick={() => {
                            fileInputRef.current?.click();
                            onToggleMediaOptions();
                        }}
                    >
                        📎 Fichier
                    </button>
                    <button
                        className="wzc-media-btn"
                        onClick={() => {
                            onStartRecording();
                            onToggleMediaOptions();
                        }}
                        disabled={isRecording}
                    >
                        🎤 Audio
                    </button>
                </div>
            )}

            {/* Audio Recording UI */}
            {isRecording && (
                <div className="wzc-recording-ui">
                    <div className="wzc-recording-indicator">
                        <div className="wzc-recording-pulse"></div>
                        <span>Enregistrement: {formatRecordingTime(recordingDuration)}</span>
                        <div className="wzc-recording-level">
                            <div
                                className="wzc-recording-level-bar"
                                style={{ width: `${Math.min(recordingLevel * 100, 100)}%` }}
                            ></div>
                        </div>
                    </div>
                    <div className="wzc-recording-controls">
                        <button
                            className="wzc-recording-stop"
                            onClick={onStopRecording}
                        >
                            ⏹️ Arrêter
                        </button>
                        <button
                            className="wzc-recording-cancel"
                            onClick={onCancelRecording}
                        >
                            ❌ Annuler
                        </button>
                    </div>
                </div>
            )}

            {/* Emoji Picker */}
            {showEmojiPicker && (
                <div className="wzc-emoji-picker">
                    <div className="wzc-emoji-grid">
                        <div className="wzc-emoji-section">
                            <span>Smileys:</span>
                            {['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰'].map(emoji => (
                                <button
                                    key={emoji}
                                    className="wzc-emoji-btn"
                                    onClick={() => onEmojiSelect(emoji)}
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                        <div className="wzc-emoji-section">
                            <span>Cœurs:</span>
                            {['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💟'].map(emoji => (
                                <button
                                    key={emoji}
                                    className="wzc-emoji-btn"
                                    onClick={() => onEmojiSelect(emoji)}
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                        <div className="wzc-emoji-section">
                            <span>Mains:</span>
                            {['👍', '👎', '👌', '🙌', '👏', '🙏', '💪', '🤝', '✋', '👋', '🤚', '🖐️', '✌️', '🤞', '🤟'].map(emoji => (
                                <button
                                    key={emoji}
                                    className="wzc-emoji-btn"
                                    onClick={() => onEmojiSelect(emoji)}
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Input Controls */}
            <div className="wzc-input-controls">
                <button
                    className="wzc-control-btn"
                    onClick={onToggleMediaOptions}
                    title="Joindre un fichier"
                >
                    📎
                </button>

                <button
                    className="wzc-control-btn"
                    onClick={onToggleEmojiPicker}
                    title="Ajouter un emoji"
                >
                    😊
                </button>

                <textarea
                    value={replyContent}
                    onChange={onReplyChange}
                    placeholder="Tapez votre message ici..."
                    className="wzc-textarea"
                    rows={1}
                    disabled={disabled || isRecording}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            onReplySubmit();
                        }
                    }}
                />

                <button
                    onClick={onReplySubmit}
                    className="wzc-send-btn"
                    disabled={disabled || (!replyContent.trim() && attachments.length === 0) || isRecording}
                    title="Envoyer"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                </button>
            </div>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />
        </div>
    );
};

export default MessageInput;